import Cookies from "js-cookie";
import { hrefFromUrl, isModalHash, isProgramDayHash } from "./program-url";

const tabsBlocks = document.querySelectorAll<HTMLDivElement>("[data-tabs]");
const TAB_COOKIE = "program_tab";

function initTabs(tabsBlock: HTMLDivElement) {
  if (!tabsBlock) return;

  const tablist = tabsBlock.querySelector("[role='tablist']") as HTMLElement;

  if (!tablist || tablist.children.length <= 0) return;

  const tabs = tablist.querySelectorAll<HTMLButtonElement>("button[role='tab']");
  const panels = tabsBlock.querySelectorAll<HTMLElement>(":scope > [role='tabpanel']");
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const rememberTab = tabsBlock.getAttribute("data-tabs") === "program";

  const saveTab = (tab: HTMLButtonElement) => {
    if (!rememberTab) return;
    Cookies.set(TAB_COOKIE, tab.id, { expires: 365 });
  };

  const getHashId = () => decodeURIComponent(window.location.hash.replace(/^#/, ""));

  const syncTabUrl = (tab: HTMLButtonElement, initial = false) => {
    if (!rememberTab) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (tab.id === "daily") {
      params.delete("types");
      if (!initial && !isProgramDayHash(url.hash) && !isModalHash(url.hash)) {
        url.hash = "program";
      }
    } else {
      const checked = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[name="types[]"]:checked')
      ).map((input) => input.value);
      if (checked.length > 0) {
        params.set("types", checked.join(","));
      } else {
        params.delete("types");
      }
      // On load, only a Daily Schedule day's hash (which this tab doesn't
      // show) becomes #program; other hashes belong to someone else.
      if ((!initial || isProgramDayHash(url.hash)) && !isModalHash(url.hash)) url.hash = "program";
    }

    const next = hrefFromUrl(url);
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== next) {
      window.history.replaceState(null, document.title, next);
    }
  };

  const showPanel = (panel: HTMLElement) => {
    panel.hidden = false;
    panel.setAttribute("data-tab", "show");
  };

  const hidePanel = (panel: HTMLElement) => {
    panel.hidden = true;
    panel.removeAttribute("data-tab");
  };

  const scrollToDay = (id: string, tries = 0) => {
    const day = document.getElementById(id);
    const panel = document.getElementById("daily-panel");
    if (!day || panel?.hidden || !panel?.hasAttribute("data-tab")) {
      if (tries < 30) {
        requestAnimationFrame(() => scrollToDay(id, tries + 1));
      }
      return;
    }
    if (day.getBoundingClientRect().height === 0 && tries < 30) {
      requestAnimationFrame(() => scrollToDay(id, tries + 1));
      return;
    }

    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    day.scrollIntoView({ block: "start", behavior: "auto" });
    root.style.scrollBehavior = previousBehavior;
  };

  const switchTab = (oldTab: HTMLButtonElement, newTab: HTMLButtonElement, event?: Event) => {
    if (event instanceof KeyboardEvent) {
      newTab.focus();
    }

    newTab.setAttribute("aria-selected", "true");
    newTab.setAttribute("tabindex", "0");

    oldTab.setAttribute("aria-selected", "false");
    oldTab.setAttribute("tabindex", "-1");

    const newIndex = Array.from(tabs).indexOf(newTab);
    const oldIndex = Array.from(tabs).indexOf(oldTab);

    hidePanel(panels[oldIndex]);
    showPanel(panels[newIndex]);
    saveTab(newTab);
    syncTabUrl(newTab);

    if (isMobile) {
      // tablist.setAttribute("data-mobile-open", "false");
      // const containerRect = tabsBlock.getBoundingClientRect();
      // window.scrollTo({
      //   top: containerRect.top + window.scrollY - 100,
      //   behavior: "smooth",
      // });
    }
  };

  tabs.forEach((tab, i) => {
    tab.setAttribute("aria-controls", panels[i].id);
    tab.addEventListener("click", (e) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLButtonElement;
      const currentTab = tablist.querySelector("[aria-selected='true']") as HTMLButtonElement;
      if (target !== currentTab) {
        switchTab(currentTab, target, e);
      } else if (isMobile) {
        tablist.setAttribute("data-mobile-open", "true");
      }
    });

    tab.addEventListener("keydown", (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const index = Array.from(tabs).indexOf(target);
      const isFirst = index === 0;
      const isLast = index === tabs.length - 1;
      const dir =
        e.key === "ArrowLeft"
          ? isFirst
            ? tabs.length - 1
            : index - 1
          : e.key === "ArrowRight"
            ? isLast
              ? 0
              : index + 1
            : null;

      if (dir !== null) {
        e.preventDefault();
        switchTab(target, tabs[dir] as HTMLButtonElement, e);
      }
    });
  });

  panels.forEach((panel, i) => {
    panel.setAttribute("aria-labelledby", tabs[i].id);
    hidePanel(panel);
  });

  const typesParam = new URLSearchParams(window.location.search).get("types");
  const hasTypes = Boolean(typesParam && typesParam.trim() !== "");

  const getInitialTabIndex = () => {
    if (!rememberTab) return 0;

    if (hasTypes) {
      const typesIndex = Array.from(tabs).findIndex((tab) => tab.id === "all-shows");
      return typesIndex >= 0 ? typesIndex : 0;
    }

    const hash = getHashId();
    if (isProgramDayHash(hash)) {
      const dailyIndex = Array.from(tabs).findIndex((tab) => tab.id === "daily");
      return dailyIndex >= 0 ? dailyIndex : 0;
    }

    const storedId = Cookies.get(TAB_COOKIE);
    const storedIndex = storedId
      ? Array.from(tabs).findIndex((tab) => tab.id === storedId)
      : -1;
    return storedIndex >= 0 ? storedIndex : 0;
  };

  const initialTab = getInitialTabIndex();

  tabs.forEach((tab, i) => {
    const isInitial = i === initialTab;
    tab.setAttribute("aria-selected", isInitial ? "true" : "false");
    tab.setAttribute("tabindex", isInitial ? "0" : "-1");
  });
  showPanel(panels[initialTab]);
  saveTab(tabs[initialTab]);
  syncTabUrl(tabs[initialTab], true);

  const hash = getHashId();
  if (hasTypes && tabs[initialTab].id === "all-shows") {
    requestAnimationFrame(() => {
      document.getElementById("program")?.scrollIntoView({ behavior: "auto", block: "start" });
    });
  } else if (tabs[initialTab].id === "daily" && isProgramDayHash(hash)) {
    scrollToDay(hash);
  }

  // Links elsewhere on the page to a day (the homepage's Today bar) open the
  // Daily Schedule on that day. The browser can't scroll to a day while its
  // tab is hidden, and the Shortlist widget answers the popstate a fragment
  // link fires by restoring its own scroll position, so the jump happens here
  // with pushState, as the date chips' does (js/program-dates.ts).
  if (rememberTab) {
    document.addEventListener("click", (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = (event.target as Element).closest?.<HTMLAnchorElement>("a[href^='#']");
      const id = link ? decodeURIComponent(link.hash.replace(/^#/, "")) : "";
      const dailyTab = Array.from(tabs).find((tab) => tab.id === "daily");
      const currentTab = tablist.querySelector<HTMLButtonElement>("[aria-selected='true']");
      if (!id || !isProgramDayHash(id) || !dailyTab || !currentTab) return;

      event.preventDefault();
      if (`#${id}` !== window.location.hash) {
        history.pushState(null, "", `#${id}`);
      }
      if (currentTab !== dailyTab) switchTab(currentTab, dailyTab);
      scrollToDay(id);
      const day = document.getElementById(id);
      if (day) {
        day.tabIndex = -1;
        day.focus({ preventScroll: true });
      }
    });
  }
}

tabsBlocks.forEach(initTabs);
